import * as React from "react";
import { useRouter } from "next/router";
import {
  ICountryCategory,
  IRoamingInternationalCountry,
  IZone,
} from "@nju/data-access-portal-dashboard";
import { IOfferZone } from "@nju/data-access-portal-offers";
import { Icon } from "@nju/icons";
import { Button, Panel, Separator, Text } from "@nju/ui";
import { ZoneCountry } from "./ZoneCountries";

interface IAreaDetailsProps {
  userId: string | undefined;
  title: string;
  category: ICountryCategory;
  exoCountries: IRoamingInternationalCountry[];
  stdCountries: IRoamingInternationalCountry[];
  euCountries: IRoamingInternationalCountry[];
  zone: IOfferZone | undefined;
  countryFromUrl: string | undefined;
  zones: IZone[] | undefined;
}

export function AreaDetails({
  userId,
  title,
  category,
  zone,
  stdCountries,
  exoCountries,
  euCountries,
  countryFromUrl,
  zones,
}: IAreaDetailsProps) {
  const router = useRouter();
  const euZoneTitle = zones?.find((zone) => {
    return zone.category === category && zone.name === "EU";
  })?.displayName.pl;
  const euZoneDescription = zones?.find((zone) => {
    return zone.category === category && zone.name === "EU";
  })?.description.pl;
  const stdZoneTitle = zones?.find((zone) => {
    return zone.category === category && zone.name === "STD";
  })?.displayName.pl;
  const stdZoneDescription = zones?.find((zone) => {
    return zone.category === category && zone.name === "STD";
  })?.description.pl;
  const exoZoneTitle = zones?.find((zone) => {
    return zone.category === category && zone.name === "EXO";
  })?.displayName.pl;
  const exoZoneDescription = zones?.find((zone) => {
    return zone.category === category && zone.name === "EXO";
  })?.description.pl;

  return (
    <>
      <Button
        onPress={() => {
          return router.back();
        }}
        size="inline"
        variant="simple"
      >
        <div className="flex items-center mb-5">
          <Icon
            className="mr-3"
            height={23}
            name="arrow-left"
            tone="accent"
            width={19}
          />
          <Text tone="accent">wstecz</Text>
        </div>
      </Button>
      <Text
        size={{ "@onlyMobile": "keyS", "@fromTablet": "keyM" }}
        weight="bold"
      >
        {title}
      </Text>
      <Separator size="xs" tone="invisible" />
      <div className="flex gap-12 flex-col from-desktop:flex-row">
        <div className="flex-[2]">
          <Panel
            iconName="earth-europe"
            initiallyExpanded={zone === "EU"}
            isFoldable={true}
            testId="eu-tariff"
            textCollapsed="ukryj kraje"
            textExpanded="pokaż kraje"
            title={euZoneTitle}
          >
            {({ isExpanded }) => {
              return isExpanded ? (
                <>
                  {euZoneDescription}
                  <Separator size="s" />
                  <div className="flex gap-2 flex-wrap">
                    {euCountries.map((country) => {
                      return (
                        <ZoneCountry
                          category={category}
                          country={country}
                          countryFromUrl={countryFromUrl}
                          key={country.displayName.pl}
                          userId={userId}
                          zone="EU"
                        />
                      );
                    })}
                  </div>
                </>
              ) : (
                <>{euZoneDescription}</>
              );
            }}
          </Panel>
          <Separator size="xs" tone="invisible" />
          <Panel
            iconName="suitcase-rolling"
            initiallyExpanded={zone === "STD"}
            isFoldable={true}
            testId="standard-tariff"
            textCollapsed="ukryj kraje"
            textExpanded="pokaż kraje"
            title={stdZoneTitle}
          >
            {({ isExpanded }) => {
              return isExpanded ? (
                <>
                  {stdZoneDescription}
                  <Separator size="s" />
                  <div className="flex gap-2 flex-wrap">
                    {stdCountries.map((country) => {
                      return (
                        <ZoneCountry
                          category={category}
                          country={country}
                          countryFromUrl={countryFromUrl}
                          key={country.displayName.pl}
                          userId={userId}
                          zone="STD"
                        />
                      );
                    })}
                  </div>
                </>
              ) : (
                <>{stdZoneDescription}</>
              );
            }}
          </Panel>
          <Separator size="xs" tone="invisible" />
          <Panel
            iconName="tree-palm"
            initiallyExpanded={zone === "EXO"}
            isFoldable={true}
            testId="exotic-tariff"
            textCollapsed="ukryj kraje"
            textExpanded="pokaż kraje"
            title={exoZoneTitle}
          >
            {({ isExpanded }) => {
              return isExpanded ? (
                <>
                  {exoZoneDescription}
                  <Separator size="s" />
                  <div className="flex gap-2 flex-wrap">
                    {exoCountries.map((country) => {
                      return (
                        <ZoneCountry
                          category={category}
                          country={country}
                          countryFromUrl={countryFromUrl}
                          key={country.displayName.pl}
                          userId={userId}
                          zone="EXO"
                        />
                      );
                    })}
                  </div>
                </>
              ) : (
                <>{exoZoneDescription}</>
              );
            }}
          </Panel>
        </div>
      </div>
    </>
  );
}
